import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, Search, ShoppingBag, Instagram, X, UserRound } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "./ui/sheet";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { useCart } from "../context/CartContext";
import { useCategories } from "../lib/hooks";
import { INSTAGRAM_URL, api } from "../lib/api";

const SearchDialog = ({ open, onOpenChange }) => {
  const [q, setQ] = useState("");
  const nav = useNavigate();
  const submit = (e) => {
    e.preventDefault();
    onOpenChange(false);
    nav(`/boutique?q=${encodeURIComponent(q.trim())}`);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0B0B0B] border-[#C9A45C]/30 sm:max-w-xl" data-testid="search-dialog">
        <DialogTitle className="eyebrow">Rechercher un parfum</DialogTitle>
        <form onSubmit={submit} className="flex items-center gap-3 border-b border-[#C9A45C]/40 pb-2">
          <Search className="h-5 w-5 text-[#C9A45C]" />
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nom, famille olfactive…"
            className="flex-1 bg-transparent outline-none font-display text-2xl placeholder:text-[#F8F6F1]/30" data-testid="search-input" />
          <button type="submit" className="eyebrow" data-testid="search-submit">OK</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const Header = () => {
  const { count, setOpen } = useCart();
  const cats = useCategories();
  const [menu, setMenu] = useState(false);
  const [search, setSearch] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [testMode, setTestMode] = useState(false);
  useEffect(() => { api.get("/config").then((r) => setTestMode(r.data.test_mode)).catch(() => {}); }, []);
  const loc = useLocation();

  useEffect(() => { setMenu(false); }, [loc.pathname]);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  const linkCls = ({ isActive }) =>
    `text-[11px] tracking-[0.22em] uppercase transition-colors duration-300 ${isActive ? "text-[#C9A45C]" : "text-[#F8F6F1]/75 hover:text-[#C9A45C]"}`;

  return (
    <header className="sticky top-0 z-50" data-testid="site-header">
      <div className="bg-[#C9A45C] text-[#0B0B0B] text-[10px] sm:text-[11px] tracking-[0.2em] uppercase text-center py-2 px-4" data-testid="announcement-bar">
        {testMode ? "Version de démonstration · Paiements en mode test · Retrait gratuit en main propre" : "Retrait gratuit en main propre à Commercy · Livraison offerte dès 50 €"}
      </div>
      <div className={`border-b border-[#C9A45C]/15 backdrop-blur-xl transition-colors duration-500 ${scrolled ? "bg-[#0B0B0B]/90" : "bg-[#0B0B0B]/70"}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 sm:h-20 flex items-center justify-between gap-4">
          <button className="lg:hidden p-2 -ml-2 text-[#F8F6F1]" onClick={() => setMenu(true)} aria-label="Ouvrir le menu" data-testid="mobile-menu-button">
            <Menu className="h-5 w-5" />
          </button>
          <Link to="/" className="flex items-center gap-3" data-testid="header-logo" aria-label="NasDeal — accueil">
            <span className="block h-12 w-12 sm:h-16 sm:w-16 overflow-hidden bg-black"><img src="/logo-nasdeal.jpg" alt="Logo NasDeal" className="h-full w-full object-cover scale-[1.3]" /></span>
          </Link>
          <nav className="hidden lg:flex items-center gap-7" aria-label="Navigation principale">
            <NavLink to="/boutique" className={linkCls} data-testid="nav-boutique">Boutique</NavLink>
            {cats.map((c) => (
              <NavLink key={c.slug} to={`/categorie/${c.slug}`} className={linkCls} data-testid={`nav-cat-${c.slug}`}>{c.short_name || c.name}</NavLink>
            ))}
            <NavLink to="/marches" className={linkCls} data-testid="nav-markets">Marchés</NavLink>
            <NavLink to="/contact" className={linkCls} data-testid="nav-contact">Contact</NavLink>
          </nav>
          <div className="flex items-center gap-1 sm:gap-2">
            <button onClick={() => setSearch(true)} className="p-2 text-[#F8F6F1]/85 hover:text-[#C9A45C] transition-colors" aria-label="Rechercher" data-testid="search-open-button">
              <Search className="h-5 w-5" />
            </button>
            <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="hidden sm:block p-2 text-[#F8F6F1]/85 hover:text-[#C9A45C] transition-colors" aria-label="Instagram NasDeal" data-testid="header-instagram-link">
              <Instagram className="h-5 w-5" />
            </a>
            <button onClick={() => setOpen(true)} className="relative p-2 text-[#F8F6F1]/85 hover:text-[#C9A45C] transition-colors" aria-label="Ouvrir le panier" data-testid="cart-drawer-toggle">
              <ShoppingBag className="h-5 w-5" />
              {count > 0 && (
                <span className="absolute -top-0.5 -right-0.5 h-5 min-w-5 px-1 rounded-full bg-[#C9A45C] text-[#0B0B0B] text-[10px] font-semibold flex items-center justify-center" data-testid="cart-count-badge">{count}</span>
              )}
            </button>
            <Link to="/compte" className="ml-1 h-9 w-9 flex items-center justify-center border border-[#C9A45C]/50 text-[#C9A45C] hover:bg-[#C9A45C] hover:text-[#0B0B0B] transition-colors duration-300" aria-label="Mon compte" data-testid="header-profile-button">
              <UserRound className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>

      <Sheet open={menu} onOpenChange={setMenu}>
        <SheetContent side="left" className="bg-[#0B0B0B] border-[#C9A45C]/20 w-[85vw] max-w-sm p-0 [&>button]:hidden" data-testid="mobile-menu">
          <SheetTitle className="sr-only">Menu</SheetTitle>
          <div className="flex items-center justify-between p-5 border-b border-[#C9A45C]/15">
            <img src="/logo-nasdeal.jpg" alt="NasDeal" className="h-14 w-14" />
            <button onClick={() => setMenu(false)} aria-label="Fermer" data-testid="mobile-menu-close"><X className="h-5 w-5" /></button>
          </div>
          <nav className="flex flex-col p-6 gap-5">
            <Link to="/boutique" className="font-display text-2xl" data-testid="mobile-nav-boutique">Toute la boutique</Link>
            {cats.map((c) => (
              <Link key={c.slug} to={`/categorie/${c.slug}`} className="font-display text-2xl text-[#F8F6F1]/85 hover:text-[#C9A45C]" data-testid={`mobile-nav-cat-${c.slug}`}>{c.name}</Link>
            ))}
            <div className="gold-line my-2" />
            <Link to="/marches" className="eyebrow !text-[#F8F6F1]/70">Nos prochains marchés</Link>
            <Link to="/compte" className="eyebrow !text-[#F8F6F1]/70">Mon compte</Link>
            <Link to="/a-propos" className="eyebrow !text-[#F8F6F1]/70">À propos</Link>
            <Link to="/contact" className="eyebrow !text-[#F8F6F1]/70">Nous contacter</Link>
            <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 eyebrow"><Instagram className="h-4 w-4" /> @NasDeal_</a>
          </nav>
        </SheetContent>
      </Sheet>
      <SearchDialog open={search} onOpenChange={setSearch} />
    </header>
  );
};
